import {
  collection,
  doc,
  getCountFromServer,
  runTransaction,
  serverTimestamp,
} from 'firebase/firestore';

export const DEFAULT_WALLET = Object.freeze({
  goldCoins: 50,
  quantumShards: 0,
  galaxyKeys: 0,
  researchPoints: 0,
  timePod: 0,
  currentLevel: 0,
  schemaVersion: 1,
  lastDailyClaimAt: null,
  lastEventId: 'initial_grant',
});

export const INITIAL_GRANT_CHANGES = Object.freeze({
  goldCoins: DEFAULT_WALLET.goldCoins,
  quantumShards: DEFAULT_WALLET.quantumShards,
  galaxyKeys: DEFAULT_WALLET.galaxyKeys,
  researchPoints: DEFAULT_WALLET.researchPoints,
  timePod: DEFAULT_WALLET.timePod,
});

const LOCAL_WALLET_KEY_PREFIX = 'sm_cascadejoy_local_wallet:';
const DEFAULT_COSMETIC_COLLECTIBLES = 0;

function isFiniteNumber(value) {
  return typeof value === 'number' && Number.isFinite(value);
}

export function normalizeWallet(value) {
  const wallet = value && typeof value === 'object' ? value : {};

  return {
    ...wallet,
    goldCoins: isFiniteNumber(wallet.goldCoins)
      ? wallet.goldCoins
      : DEFAULT_WALLET.goldCoins,
    quantumShards: isFiniteNumber(wallet.quantumShards)
      ? wallet.quantumShards
      : DEFAULT_WALLET.quantumShards,
    galaxyKeys: isFiniteNumber(wallet.galaxyKeys)
      ? wallet.galaxyKeys
      : DEFAULT_WALLET.galaxyKeys,
    researchPoints: isFiniteNumber(wallet.researchPoints)
      ? wallet.researchPoints
      : DEFAULT_WALLET.researchPoints,
    timePod: isFiniteNumber(wallet.timePod)
      ? wallet.timePod
      : DEFAULT_WALLET.timePod,
    currentLevel: isFiniteNumber(wallet.currentLevel)
      ? wallet.currentLevel
      : DEFAULT_WALLET.currentLevel,
    cosmeticCollectibles: isFiniteNumber(wallet.cosmeticCollectibles)
      ? wallet.cosmeticCollectibles
      : DEFAULT_COSMETIC_COLLECTIBLES,
    schemaVersion: Number.isInteger(wallet.schemaVersion)
      ? wallet.schemaVersion
      : DEFAULT_WALLET.schemaVersion,
    lastDailyClaimAt:
      Object.prototype.hasOwnProperty.call(wallet, 'lastDailyClaimAt')
        ? wallet.lastDailyClaimAt
        : DEFAULT_WALLET.lastDailyClaimAt,
    lastEventId:
      typeof wallet.lastEventId === 'string' && wallet.lastEventId
        ? wallet.lastEventId
        : DEFAULT_WALLET.lastEventId,
  };
}

export function getLocalWalletKey(uid) {
  return `${LOCAL_WALLET_KEY_PREFIX}${encodeURIComponent(String(uid))}`;
}

function getBrowserStorage() {
  if (typeof window === 'undefined') return null;

  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

export function loadOrCreateLocalWallet(uid, storage = getBrowserStorage()) {
  if (!uid) throw new Error('A user ID is required to load a reward wallet.');

  const key = getLocalWalletKey(uid);

  if (storage) {
    try {
      const savedWallet = storage.getItem(key);
      if (savedWallet) return normalizeWallet(JSON.parse(savedWallet));
    } catch {
      // A malformed or inaccessible local wallet is safely replaced below.
    }
  }

  const timestamp = new Date().toISOString();
  const initialWallet = {
    ...DEFAULT_WALLET,
    cosmeticCollectibles: DEFAULT_COSMETIC_COLLECTIBLES,
    createdAt: timestamp,
    updatedAt: timestamp,
  };

  if (storage) {
    try {
      storage.setItem(key, JSON.stringify(initialWallet));
    } catch {
      // Local mode can still render an in-memory wallet when storage is unavailable.
    }
  }

  return initialWallet;
}

export async function loadOrCreateRemoteWallet(firestore, uid) {
  if (!firestore) throw new Error('Firestore is not available.');
  if (!uid) throw new Error('A user ID is required to load a reward wallet.');

  const walletRef = doc(firestore, 'users', uid, 'economy', 'wallet');
  const initialGrantRef = doc(
    firestore,
    'users',
    uid,
    'rewardEntries',
    'initial_grant'
  );

  const wallet = await runTransaction(firestore, async (transaction) => {
    const walletSnapshot = await transaction.get(walletRef);

    if (walletSnapshot.exists()) {
      return normalizeWallet(walletSnapshot.data());
    }

    const timestamp = serverTimestamp();
    transaction.set(walletRef, {
      ...DEFAULT_WALLET,
      createdAt: timestamp,
      updatedAt: timestamp,
    });
    transaction.set(initialGrantRef, {
      type: 'initial_grant',
      sourceId: 'account_created',
      changes: { ...INITIAL_GRANT_CHANGES },
      scoreAwarded: 0,
      schemaVersion: 1,
      createdAt: timestamp,
    });

    return {
      ...DEFAULT_WALLET,
      createdAt: null,
      updatedAt: null,
    };
  });

  const cosmeticsRef = collection(firestore, 'users', uid, 'cosmetics');
  const cosmeticsSnapshot = await getCountFromServer(cosmeticsRef);

  return normalizeWallet({
    ...wallet,
    cosmeticCollectibles: cosmeticsSnapshot.data().count,
  });
}
